import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getMyAgent, syncCalls } from '../../api/telephony/cloudtalk.js';
import CloudTalkPhone from '../../components/telephony/CloudTalkPhone.jsx';
import { TelephonyContext } from './telephony-context.js';

// All real talking happens in the background CloudTalk phone (widget below).
// ERP buttons dial directly via the API; notes live in the call history.
// There is no dialer modal anymore — the widget + history cover the flow.
export const TelephonyProvider = ({ children }) => {
    // Bumped whenever the background CloudTalk phone reports a finished call.
    const [callsRefreshKey, setCallsRefreshKey] = useState(0);
    // Last ERP-initiated outbound dial { to_number, label, at }. CloudTalk
    // rings the AGENT first (callback leg), so an inbound-looking ring within
    // ~90s of our own dial is the agent leg of that outbound call — not a
    // real incoming call. The widget uses this to label it correctly.
    const [pendingOutbound, setPendingOutbound] = useState(null);
    const [agentInfo, setAgentInfo] = useState({ has_agent: false, agent_id: null, loaded: false });

    const refreshAgent = useCallback(async () => {
        try {
            const data = await getMyAgent();
            setAgentInfo({
                has_agent: Boolean(data?.has_agent),
                agent_id: data?.agent_id || null,
                loaded: true,
            });
        } catch {
            setAgentInfo({ has_agent: false, agent_id: null, loaded: true });
        }
    }, []);

    useEffect(() => {
        refreshAgent();
    }, [refreshAgent]);

    const noteOutboundAttempt = (info) => {
        setPendingOutbound(info ? { ...info, at: Date.now() } : null);
    };

    // Ask the background phone widget to expand (e.g. direct-call failures
    // asking the user to log in). No-op when the widget isn't mounted.
    const expandCloudPhone = () => {
        window.dispatchEvent(new Event('cloudtalk-phone:expand'));
    };

    const contextValue = useMemo(
        () => ({
            expandCloudPhone,
            noteOutboundAttempt,
            pendingOutbound,
            callsRefreshKey,
            hasAgent: agentInfo.has_agent,
            agentId: agentInfo.agent_id,
            agentLoaded: agentInfo.loaded,
            refreshAgent,
        }),
        [callsRefreshKey, pendingOutbound, agentInfo, refreshAgent],
    );

    return (
        <TelephonyContext.Provider value={contextValue}>
            {children}
            {/* Background CloudTalk softphone: mounted once, kept alive hidden,
                so the agent stays online and ERP calls ring here. */}
            <CloudTalkPhone
                hasAgent={agentInfo.has_agent}
                pendingOutbound={pendingOutbound}
                onCallEnded={() => {
                    setCallsRefreshKey((k) => k + 1);
                    setPendingOutbound(null);
                    // CloudTalk takes a few seconds to finalize CDR / wrap-up notes; sync immediately and after 8s
                    syncCalls().catch(() => {});
                    setTimeout(() => {
                        syncCalls().then(() => setCallsRefreshKey((k) => k + 1)).catch(() => {});
                    }, 8000);
                }}
            />
        </TelephonyContext.Provider>
    );
};

TelephonyProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export default TelephonyProvider;
